import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { verkaufenContent } from '../data/pageContent';

const UnternehmenVerkaufenPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Helmet>
        <title>{verkaufenContent.title}</title>
        <meta name="description" content={verkaufenContent.description} />
        <link rel="canonical" href="https://www.unternehmenbewerten.ch/unternehmen-verkaufen" />
      </Helmet>
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 lg:py-24 bg-gradient-to-b from-[#f8f9fa] to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <nav className="text-sm text-[#5a5d5b] mb-4">
                <Link to="/" className="hover:text-[#FF0000]">Home</Link>
                <span className="mx-2">/</span>
                <span>Unternehmen verkaufen</span>
              </nav>
              <h1 className="text-4xl sm:text-5xl font-bold text-[#1a1c1b] mb-6">
                {verkaufenContent.h1}
              </h1>
              <p className="text-lg text-[#5a5d5b] mb-4">
                {verkaufenContent.intro}
              </p>
              <p className="text-[#5a5d5b] mb-8">
                {verkaufenContent.introExtended}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/bewertung" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#FF0000] text-white font-semibold rounded-lg hover:bg-[#e60000] transition-colors">
                  Unternehmenswert berechnen
                  <ArrowRight size={18} />
                </Link>
                <Link to="/kontakt" className="inline-flex items-center justify-center px-6 py-3 border-2 border-[#1a1c1b] text-[#1a1c1b] font-semibold rounded-lg hover:bg-[#1a1c1b] hover:text-white transition-colors">
                  Verkaufsprozess starten
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Unser Verkaufsprozess */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-4 text-center">
              Unser Verkaufsprozess
            </h2>
            <p className="text-[#5a5d5b] text-center mb-8 max-w-3xl mx-auto">
              {verkaufenContent.processIntro}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {verkaufenContent.processSteps.map((step) => (
                <div key={step.step} className="bg-[#f8f9fa] rounded-xl p-6 border border-gray-100">
                  <div className="w-10 h-10 rounded-full bg-[#FF0000] text-white flex items-center justify-center font-bold mb-4">
                    {step.step}
                  </div>
                  {step.link ? (
                    <Link to={step.link} className="text-[#1a1c1b] font-semibold mb-2 block hover:text-[#FF0000]">{step.title}</Link>
                  ) : (
                    <h3 className="text-[#1a1c1b] font-semibold mb-2">{step.title}</h3>
                  )}
                  <p className="text-[#5a5d5b] text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Typische Gründe für einen Unternehmensverkauf */}
        <section className="py-16 bg-[#f8f9fa]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-4 text-center">
              Typische Gründe für einen Unternehmensverkauf
            </h2>
            <p className="text-[#5a5d5b] text-center mb-8 max-w-3xl mx-auto">
              {verkaufenContent.sellingReasonsIntro}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {verkaufenContent.sellingReasons.map((reason) => (
                <div key={reason.title} className="bg-white rounded-xl p-6 border border-gray-100">
                  <h3 className="text-[#FF0000] font-semibold mb-2">{reason.title}</h3>
                  <p className="text-[#5a5d5b] text-sm">{reason.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Worauf Käufer achten */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-4 text-center">
              Worauf Käufer achten
            </h2>
            <p className="text-[#5a5d5b] text-center mb-8">
              {verkaufenContent.buyerInterestsIntro}
            </p>
            <div className="bg-[#f8f9fa] rounded-2xl p-8 border border-gray-100">
              <ul className="space-y-4">
                {verkaufenContent.buyerInterests.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#FF0000] flex-shrink-0 mt-0.5" />
                    <span className="text-[#1a1c1b]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Optimaler Zeitpunkt für den Verkauf */}
        <section className="py-16 bg-[#f8f9fa]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-4 text-center">
              Optimaler Zeitpunkt für den Verkauf
            </h2>
            <p className="text-[#5a5d5b] text-center mb-8">
              {verkaufenContent.optimalTimingIntro}
            </p>
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <ul className="space-y-4">
                {verkaufenContent.optimalTiming.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#FF0000] flex-shrink-0 mt-0.5" />
                    <span className="text-[#1a1c1b]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Ihre Vorteile */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-6 text-center">
              Ihre Vorteile bei unternehmenbewerten.ch
            </h2>
            <p className="text-[#5a5d5b] leading-relaxed mb-8">
              {verkaufenContent.trustSection}
            </p>
            <p className="text-[#5a5d5b]">
              Sie möchten zuerst den Wert Ihrer Firma kennen? Nutzen Sie unsere{' '}
              <Link to="/bewertung" className="text-[#FF0000] hover:underline">kostenlose Firmenbewertung</Link> als ersten Schritt. Oder{' '}
              <Link to="/kontakt" className="text-[#FF0000] hover:underline">kontaktieren Sie uns</Link> für ein persönliches Erstgespräch.
            </p>
            <div className="text-center mt-8">
              <Link to="/kontakt" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#FF0000] text-white font-semibold rounded-lg hover:bg-[#e60000] transition-colors">
                Verkaufsprozess starten – Erstgespräch anfragen
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>

        {/* Häufige Fragen */}
        <section className="py-16 bg-[#f8f9fa]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-8 text-center">
              Häufige Fragen zum Firmenverkauf
            </h2>
            <div className="space-y-6">
              {verkaufenContent.faq.map((item) => (
                <div key={item.question} className="bg-white rounded-xl p-6 border border-gray-100">
                  <h3 className="text-[#1a1c1b] font-semibold mb-3">{item.question}</h3>
                  <p className="text-[#5a5d5b] leading-relaxed">{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Referenzen Teaser */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-[#f8f9fa] rounded-2xl p-8 border border-gray-100">
              <h3 className="text-xl font-bold text-[#1a1c1b] mb-4">So läuft ein Verkauf in der Praxis ab</h3>
              <p className="text-[#5a5d5b] mb-6">Erfahren Sie anhand anonymisierter Praxisbeispiele, wie ein Verkauf an einen Mitbewerber oder an eine Investorengruppe ablaufen kann.</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/referenzen#verkauf-mitbewerber" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF0000] text-white font-semibold rounded-lg hover:bg-[#e60000] transition-colors text-sm">
                  Verkauf an Mitbewerber
                  <ArrowRight size={16} />
                </Link>
                <Link to="/referenzen#verkauf-investorengruppe" className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#FF0000] text-[#FF0000] font-semibold rounded-lg hover:bg-[#FF0000] hover:text-white transition-colors text-sm">
                  Verkauf an Investoren
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#1a1c1b]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Bereit für Ihren Unternehmensverkauf?
            </h2>
            <p className="text-gray-300 mb-8">
              Kontaktieren Sie uns für ein unverbindliches Erstgespräch.
            </p>
            <Link to="/kontakt" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#FF0000] text-white font-semibold rounded-lg hover:bg-[#e60000] transition-colors">
              Erstgespräch anfragen
              <ArrowRight size={18} />
            </Link>
          </div>
        </section>
      </main>
      
      <Footer />
      {/* Schemas werden ausschliesslich via SSG geliefert - keine Duplikate durch React */}
    </div>
  );
};

export default UnternehmenVerkaufenPage;
